import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useBrandReadiness } from "../../hooks/useBrandReadiness";
import { buildBrandArtifactPath } from "../../lib/brand-navigation";
import type { BrandReadinessMissingKey } from "../../../../shared/types.ts";

const DISMISS_KEY_PREFIX = "brand-readiness-banner";

function dismissKey(brandId: string): string {
  return `${DISMISS_KEY_PREFIX}:${brandId}`;
}

function labelForMissing(item: BrandReadinessMissingKey): string {
  return item === "voice" ? "Voice" : "Context Docs";
}

export function BrandReadinessBanner(props: {
  brandId: string;
  missingOverride?: BrandReadinessMissingKey[] | null;
}) {
  const { brandId, missingOverride = null } = props;
  const readinessQuery = useBrandReadiness(brandId);
  const readiness = readinessQuery.data?.data;
  const [dismissed, setDismissed] = useState(false);
  const forcedMissing = missingOverride && missingOverride.length > 0 ? missingOverride : null;

  useEffect(() => {
    setDismissed(false);
  }, [brandId, readiness?.isReady, readiness?.hasContextDocs, readiness?.hasVoice]);

  useEffect(() => {
    if (!readiness?.isReady) {
      localStorage.removeItem(dismissKey(brandId));
      return;
    }
    if (localStorage.getItem(dismissKey(brandId)) === "dismissed") {
      setDismissed(true);
    }
  }, [brandId, readiness?.isReady]);

  const missing = useMemo(() => {
    if (forcedMissing) return forcedMissing;
    if (!readiness) return [];
    const next: BrandReadinessMissingKey[] = [];
    if (!readiness.hasVoice) next.push("voice");
    if (!readiness.hasContextDocs) next.push("context_docs");
    return next;
  }, [forcedMissing, readiness]);

  if (dismissed || missing.length === 0) {
    return null;
  }

  if (forcedMissing && readiness?.isReady) {
    return null;
  }

  if (!forcedMissing && (!readiness || readiness.isReady)) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-950">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="font-medium">
            Your agent does not know this brand well enough yet.
          </p>
          <p className="text-amber-900">
            Missing: {missing.map(labelForMissing).join(" · ")}. Add voice guidance or context docs for better drafts and reviews.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              to={buildBrandArtifactPath(brandId, "voice")}
              className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Open Voice
            </Link>
            <Link
              to={`/brand/${encodeURIComponent(brandId)}/settings/context-docs`}
              className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Open Context Docs
            </Link>
            {readiness && readiness.hasCorpus === false ? (
              <span className="rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-xs text-amber-900">
                Corpus recommended, not required
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            if (readiness?.isReady) {
              localStorage.setItem(dismissKey(brandId), "dismissed");
            }
          }}
          className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
